import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';

import { useActionSettings } from '../../hooks/useActionSettings';
import { useWebSocket } from '../WebSocketContext';
import ActionSettingsLayout from '../ActionSettingsHeader';
import TagsDropdown from '../TagsDropdown';
import TagEditor from '../Widgets/TagEditor';
import { buildGetExportSettingsPayload } from '../../Utils/actionSettingsUtils';

const ActionsOutlookMoveEmail = ({ caseid, tags, settingId, onSaveStatusChange }) => {
  const { t } = useTranslation();
  const { ws, subscribeToMessages, sendMessage } = useWebSocket();

  const [accountEmail, setAccountEmail] = useState('');
  const [messageId, setMessageId] = useState('');
  const [messageIdType, setMessageIdType] = useState('auto');
  const [targetFolder, setTargetFolder] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [pendingDisconnect, setPendingDisconnect] = useState(false);
  const [verified, setVerified] = useState(false);
  const [isAuthorizing, setIsAuthorizing] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [foldersError, setFoldersError] = useState('');
  const [tagMenuAnchor, setTagMenuAnchor] = useState(null);
  const [tagTarget, setTagTarget] = useState(null);

  const messageIdRef = useRef(null);

  const customComparisonCallback = useCallback((initial = {}) => {
    const initialAuth = initial.outlookAuth || {};
    return (
      accountEmail === (initialAuth.account || '') &&
      verified === !!initialAuth.verified &&
      pendingDisconnect === false &&
      messageId === (initial.messageId || '') &&
      messageIdType === (initial.messageIdType || 'auto') &&
      targetFolder === (initial.targetFolderPath || initial.targetFolder || '')
    );
  }, [accountEmail, verified, pendingDisconnect, messageId, messageIdType, targetFolder]);

  const customSaveDataCallback = useCallback(() => ({
    messageId,
    messageIdType,
    targetFolderPath: targetFolder,
    outlookAuth: {
      account: accountEmail,
      verified,
      ...(pendingDisconnect ? { disconnect: true } : {})
    }
  }), [accountEmail, verified, pendingDisconnect, messageId, messageIdType, targetFolder]);

  const loadDataCallback = useCallback((response) => {
    if (!response?.customData) return;
    const data = response.customData;
    const outlookAuth = data.outlookAuth || {};

    setMessageId(data.messageId || '');
    setMessageIdType(data.messageIdType || 'auto');
    setTargetFolder(data.targetFolderPath || data.targetFolder || '');
    setAccountEmail(outlookAuth.account || outlookAuth.preferredUsername || '');
    setVerified(!!outlookAuth.verified);
    setPendingDisconnect(false);

    if (outlookAuth.pendingState) {
      setConnectionStatus('pending');
      setConnectionMessage(t('workflow.outlook_move_email.awaiting_auth', 'Complete the Microsoft login flow in the opened tab.'));
    } else if (outlookAuth.verified) {
      setConnectionStatus('connected');
      setConnectionMessage(t('workflow.outlook_move_email.connected', 'Connected to Outlook account.'));
    } else {
      setConnectionStatus('disconnected');
      setConnectionMessage('');
    }
  }, [t]);

  const {
    isEnabled,
    setIsEnabled,
    conditions,
    isSaveDisabled,
    forceRunLoading,
    forceRunResult,
    popUpOpen,
    forceRunWarningOpen,
    forceRunWarningCount,
    handleSave,
    handleForceRun,
    handleForceRunConfirm,
    handleForceRunCancel,
    handleConditionsChange,
    handleCloseStatusWindow,
    id
  } = useActionSettings({
    settingId,
    caseid,
    actionType: 'moveOutlookEmail',
    onSaveStatusChange,
    customComparison: customComparisonCallback,
    customSaveData: customSaveDataCallback,
    loadDataFunction: loadDataCallback
  });

  const isPersistedAction = useMemo(() => (
    id && !(typeof id === 'string' && id.startsWith('temp_'))
  ), [id]);

  const handleConnect = () => {
    if (!ws) return;
    if (!isPersistedAction) {
      setConnectionMessage(t('workflow.outlook_move_email.save_before_connect', 'Save the action before connecting Outlook.'));
      return;
    }
    if (!accountEmail) {
      setConnectionMessage(t('workflow.outlook_move_email.enter_account', 'Enter your Outlook email before connecting.'));
      return;
    }

    setIsAuthorizing(true);
    setConnectionMessage('');
    setPendingDisconnect(false);
    let returnUrl = window.location.href;
    try {
      const url = new URL(returnUrl);
      url.searchParams.set('openActions', 'true');
      url.searchParams.set('exportId', String(id));
      returnUrl = url.toString();
    } catch (err) {
      // keep original returnUrl
    }
    ws.send(JSON.stringify({
      command: 'authorizeOutlookAction',
      caseid,
      exportId: id,
      account: accountEmail,
      returnUrl
    }));
  };

  const handleDisconnect = () => {
    setConnectionStatus('disconnected');
    setConnectionMessage(t('workflow.outlook_move_email.disconnect_pending', 'The connection will be removed after you save these settings.'));
    setPendingDisconnect(true);
    setVerified(false);
    setFolders([]);
  };

  const requestFolders = useCallback(() => {
    if (!ws || !isPersistedAction || connectionStatus !== 'connected') {
      return;
    }
    setFoldersLoading(true);
    setFoldersError('');
    ws.send(JSON.stringify({
      command: 'getOutlookActionFolders',
      caseid,
      exportId: id
    }));
  }, [ws, isPersistedAction, connectionStatus, caseid, id]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((message) => {
      if (!message) return;

      if (message.command === 'authorizeOutlookActionStart') {
        const payload = message.data || {};
        if (payload.error) {
          setConnectionMessage(payload.error);
          setIsAuthorizing(false);
          return;
        }
        if (payload.exportId === id) {
          if (payload.url) {
            window.location.assign(payload.url);
          }
          setConnectionStatus('pending');
          setConnectionMessage(t('workflow.outlook_move_email.awaiting_auth', 'Complete the Microsoft login flow in the opened tab.'));
          setIsAuthorizing(false);
        }
      } else if (message.command === 'outlookActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(t('workflow.outlook_move_email.connected', 'Connected to Outlook account.'));
          setVerified(true);
          setPendingDisconnect(false);
          if (isPersistedAction) {
            sendMessage(buildGetExportSettingsPayload(id, caseid));
          }
        } else if (payload.error) {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error);
        }
      } else if (message.command === 'verifyOutlookActionTokenResult' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        setIsCheckingConnection(false);
        if (payload.verified) {
          setConnectionStatus('connected');
          setConnectionMessage(t('workflow.outlook_move_email.connected', 'Connected to Outlook account.'));
          setVerified(true);
          setPendingDisconnect(false);
        } else {
          setConnectionStatus('disconnected');
          setConnectionMessage(payload.error || t('workflow.outlook_move_email.invalid_connection', 'Outlook connection requires re-authentication.'));
          setVerified(false);
        }
      } else if (message.command === 'getOutlookActionFolders' && (message.data?.exportId === id)) {
        const payload = message.data || {};
        setFoldersLoading(false);
        if (payload.error) {
          setFoldersError(payload.error);
          setFolders([]);
        } else {
          setFolders(Array.isArray(payload.folders) ? payload.folders : []);
          setFoldersError('');
        }
      }
    });

    return unsubscribe;
  }, [subscribeToMessages, id, t, ws, isPersistedAction, caseid, sendMessage]);

  useEffect(() => {
    if (!ws || !isPersistedAction) {
      return;
    }
    setIsCheckingConnection(true);
    setConnectionStatus('pending');
    setConnectionMessage(t('workflow.outlook_move_email.checking_status', 'Checking Outlook connection...'));
    ws.send(JSON.stringify({
      command: 'verifyOutlookActionToken',
      caseid,
      exportId: id
    }));
  }, [ws, caseid, id, isPersistedAction, t]);

  useEffect(() => {
    if (connectionStatus === 'connected') {
      requestFolders();
    }
  }, [connectionStatus, requestFolders]);

  const handleTagMenuOpen = (event, target) => {
    setTagTarget(target);
    setTagMenuAnchor(event.currentTarget);
  };

  const handleTagSelect = (tagValue) => {
    const token = `{${tagValue}}`;
    if (tagTarget === 'messageId' && messageIdRef.current) {
      messageIdRef.current.insertTag(tagValue);
    } else if (tagTarget === 'messageId') {
      setMessageId((prev) => `${prev}${token}`);
    } else if (tagTarget === 'targetFolder') {
      setTargetFolder((prev) => `${prev}${token}`);
    }
    setTagMenuAnchor(null);
    setTagTarget(null);
  };

  const connectionChipColor = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'pending' || isCheckingConnection
      ? 'warning'
      : 'default';

  const connectionAlertSeverity = connectionStatus === 'connected'
    ? 'success'
    : connectionStatus === 'disconnected'
      ? 'error'
      : 'info';

  const selectedFolderOption = useMemo(() => (
    folders.find((folder) => folder.path === targetFolder) || null
  ), [folders, targetFolder]);

  return (
    <>
      <ActionSettingsLayout
        isEnabled={isEnabled}
        onEnabledChange={setIsEnabled}
        title={t('workflow.move_outlook_email.title', 'Move email (Outlook)')}
        isSaveDisabled={isSaveDisabled}
        onSave={handleSave}
        onForceRun={handleForceRun}
        forceRunLoading={forceRunLoading}
        conditions={conditions}
        onConditionsChange={handleConditionsChange}
        tags={tags}
        popUpOpen={popUpOpen}
        forceRunResult={forceRunResult}
        onCloseStatusWindow={handleCloseStatusWindow}
        forceRunWarningOpen={forceRunWarningOpen}
        forceRunWarningCount={forceRunWarningCount}
        onForceRunConfirm={handleForceRunConfirm}
        onForceRunCancel={handleForceRunCancel}
        primaryColor="#4a6df0"
      >
        <>
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2} gap={2}>
                  <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                    {t('workflow.outlook_move_email.connection', 'Outlook connection')}
                  </Typography>
                  <Chip
                    size="small"
                    color={connectionChipColor}
                    label={connectionStatus === 'connected'
                      ? t('workflow.outlook_move_email.status_connected', 'Connected')
                      : connectionStatus === 'pending'
                        ? t('workflow.outlook_move_email.status_pending', 'Pending')
                        : t('workflow.outlook_move_email.status_disconnected', 'Disconnected')}
                  />
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label={t('workflow.outlook_move_email.account_email', 'Outlook email')}
                      value={accountEmail}
                      onChange={(e) => setAccountEmail(e.target.value)}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6} display="flex" alignItems="center" gap={2}>
                    <Button
                      variant="contained"
                      startIcon={<LinkIcon />}
                      onClick={handleConnect}
                      disabled={isAuthorizing || isCheckingConnection || connectionStatus === 'connected'}
                    >
                      {t('workflow.outlook_move_email.connect', 'Connect Outlook')}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<LinkOffIcon />}
                      onClick={handleDisconnect}
                      disabled={connectionStatus === 'disconnected'}
                    >
                      {t('workflow.outlook_move_email.disconnect', 'Disconnect')}
                    </Button>
                  </Grid>
                  {connectionMessage && (
                    <Grid item xs={12}>
                      <Alert severity={connectionAlertSeverity}>{connectionMessage}</Alert>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>{t('workflow.outlook_move_email.message_id_type', 'Message identifier')}</InputLabel>
              <Select
                label={t('workflow.outlook_move_email.message_id_type', 'Message identifier')}
                value={messageIdType}
                onChange={(event) => setMessageIdType(event.target.value)}
              >
                <MenuItem value="auto">{t('workflow.outlook_move_email.message_id_auto', 'Auto')}</MenuItem>
                <MenuItem value="graphId">{t('workflow.outlook_move_email.message_id_graph', 'Graph message id')}</MenuItem>
                <MenuItem value="internetMessageId">{t('workflow.outlook_move_email.message_id_internet', 'Internet-Message-ID')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mb: 0.5, display: 'block' }}>
              {t('workflow.outlook_move_email.message_id', 'Message ID')}
            </Typography>
            <Box sx={{ borderRadius: '8px', overflow: 'hidden' }}>
              <TagEditor
                caseid={caseid}
                ref={messageIdRef}
                value={messageId}
                onChange={setMessageId}
                placeholder={t('workflow.outlook_move_email.message_id_placeholder', 'Leave empty to use the current Outlook .eml event')}
                singleLine
                tags={tags}
                onTagClick={(event) => handleTagMenuOpen(event, 'messageId')}
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                {t('workflow.outlook_move_email.target_folder', 'Target folder')}
              </Typography>
              <IconButton
                size="small"
                onClick={requestFolders}
                disabled={foldersLoading || connectionStatus !== 'connected'}
                title={t('workflow.outlook_move_email.refresh_folders', 'Refresh folders')}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>
            <Autocomplete
              freeSolo
              options={folders}
              loading={foldersLoading}
              value={selectedFolderOption || targetFolder}
              inputValue={targetFolder}
              onInputChange={(event, value) => setTargetFolder(value || '')}
              onChange={(event, value) => {
                if (typeof value === 'string') {
                  setTargetFolder(value);
                } else if (value?.path) {
                  setTargetFolder(value.path);
                } else {
                  setTargetFolder('');
                }
              }}
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return option.path || option.displayName || '';
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value || typeof value === 'string') return false;
                return option.id === value.id;
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.path}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('workflow.outlook_move_email.folder_counts', '{{total}} total, {{unread}} unread', {
                        total: option.totalItemCount || 0,
                        unread: option.unreadItemCount || 0
                      })}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('workflow.outlook_move_email.target_folder_placeholder', 'Choose a folder or enter id:<folder-id>')}
                  fullWidth
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {foldersLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
            {foldersError && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {foldersError}
              </Alert>
            )}
            {connectionStatus !== 'connected' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('workflow.outlook_move_email.connect_to_load_folders', 'Connect Outlook to load folders.')}
              </Alert>
            )}
            {connectionStatus === 'connected' && !foldersLoading && folders.length === 0 && !foldersError && (
              <Alert severity="info" sx={{ mt: 1 }}>
                {t('workflow.outlook_move_email.no_folders_loaded', 'No folders loaded yet. Use refresh to try again.')}
              </Alert>
            )}
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                onClick={(event) => handleTagMenuOpen(event, 'targetFolder')}
              >
                {t('workflow.outlook_move_email.insert_tag', 'Insert tag')}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Alert icon={<DriveFileMoveIcon />} severity="info">
              {t('workflow.outlook_move_email.help', 'Uses Microsoft Graph to move the selected Outlook message. Folder paths are matched by display name from the mailbox root.')}
            </Alert>
          </Grid>
        </>
      </ActionSettingsLayout>

      <TagsDropdown
        tags={tags}
        handleTagSelect={handleTagSelect}
        anchorEl={tagMenuAnchor}
        setAnchorEl={setTagMenuAnchor}
      />
    </>
  );
};

export default ActionsOutlookMoveEmail;
